import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Music, Send, Trash2,
  Calendar, Filter, Users, Compass, UserPlus, Repeat2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const FeedPage = () => {
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [feedType, setFeedType] = useState('discover');
  const [category, setCategory] = useState('all');
  const [suggestedProfiles, setSuggestedProfiles] = useState([]);

  useEffect(() => {
    fetchFeed();
  }, [feedType, category]);

  useEffect(() => {
    fetchSuggested();
  }, []);

  const fetchFeed = async (loadMore = false) => {
    const currentPage = loadMore ? page + 1 : 1;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url = `${API}/social/feed?page=${currentPage}&limit=20`;
      if (feedType === 'following' && token) {
        url = `${API}/social/feed/following?page=${currentPage}&limit=20`;
      }
      if (category !== 'all') url += `&category=${category}`;

      const res = await axios.get(url, { headers });
      const data = res.data;
      if (loadMore) {
        setPosts(prev => [...prev, ...(data.posts || [])]);
      } else {
        setPosts(data.posts || []);
      }
      setHasMore(data.has_more || false);
      setPage(currentPage);
    } catch {
      if (!loadMore) setPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggested = async () => {
    try {
      const res = await axios.get(`${API}/social/discover/profiles?limit=6`);
      setSuggestedProfiles(res.data.profiles || []);
    } catch { }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 pb-24" data-testid="feed-loading">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-card rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-3">
              <Skeleton className="w-10 h-10 rounded-full" />
              <Skeleton className="h-4 w-32" />
            </div>
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-24" data-testid="feed-page">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <h1 className="text-2xl font-bold">Social Feed</h1>
        <p className="text-sm text-muted-foreground">Latest from Twin Cities artists & businesses</p>
      </motion.div>

      {/* Feed Type Toggle */}
      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        <button
          onClick={() => setFeedType('discover')}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition whitespace-nowrap ${feedType === 'discover' ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-gray-300 bg-card border border-border'}`}
          data-testid="feed-discover-tab"
        >
          <Compass size={13} /> Discover
        </button>
        {token && (
          <button
            onClick={() => setFeedType('following')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition whitespace-nowrap ${feedType === 'following' ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-gray-300 bg-card border border-border'}`}
            data-testid="feed-following-tab"
          >
            <Users size={13} /> Following
          </button>
        )}
        {['music', 'food', 'events', 'vendors'].map(cat => (
          <button
            key={cat}
            onClick={() => { setCategory(cat === category ? 'all' : cat); setFeedType('discover'); }}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition capitalize whitespace-nowrap ${category === cat ? 'bg-purple-500/20 text-purple-400' : 'text-gray-500 hover:text-gray-300 bg-card border border-border'}`}
            data-testid={`feed-filter-${cat}`}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Suggested Profiles - horizontal scroll */}
      {suggestedProfiles.length > 0 && feedType === 'discover' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 overflow-x-auto no-scrollbar"
        >
          <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2">People to Follow</h3>
          <div className="flex gap-3">
            {suggestedProfiles.filter(p => p.id !== user?.id).slice(0, 6).map(p => (
              <Link
                key={p.id}
                to={`/u/${p.id}`}
                className="shrink-0 w-24 text-center group"
                data-testid={`suggested-profile-${p.id}`}
              >
                <div className="w-16 h-16 mx-auto rounded-full overflow-hidden border-2 border-border group-hover:border-purple-500/50 transition mb-1">
                  {p.profile_pic ? (
                    <img src={p.profile_pic} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-card text-lg font-bold text-gray-500">
                      {p.name?.charAt(0)}
                    </div>
                  )}
                </div>
                <p className="text-[11px] font-medium truncate group-hover:text-purple-400 transition">{p.name}</p>
                <p className="text-[9px] text-gray-500 capitalize">{p.role}</p>
                {p.badges && p.badges.length > 0 && (
                  <div className="flex justify-center gap-0.5 mt-0.5 flex-wrap">
                    {p.badges.slice(0, 2).map(b => (
                      <span key={b.key} className="text-[8px] px-1 py-px rounded-full font-bold" style={{ background: `${b.color}20`, color: b.color }}>{b.label}</span>
                    ))}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </motion.div>
      )}

      {/* Post Creation for eligible users */}
      {user && user.role !== 'user' && (
        <FeedPostBox token={token} user={user} onPost={() => fetchFeed()} />
      )}

      {/* Posts */}
      {posts.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-border bg-card/30">
          <Music size={48} className="mx-auto mb-3 text-gray-600" />
          <h3 className="text-lg font-medium mb-1">No posts yet</h3>
          <p className="text-sm text-muted-foreground">
            {feedType === 'following' ? 'Follow some accounts to see their posts here!' : 'Be the first to post or check back later!'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map((post, index) => (
            post.is_share ? (
              <SharedPostCard key={post.id || index} share={post} user={user} token={token} index={index} />
            ) : (
              <FeedPostCard key={post.id || index} post={post} user={user} token={token} index={index} onRefresh={() => fetchFeed()} />
            )
          ))}
          {hasMore && (
            <div className="text-center pt-4">
              <Button variant="outline" onClick={() => fetchFeed(true)} data-testid="load-more-btn">
                Load More
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Post creation in feed
const FeedPostBox = ({ token, user, onPost }) => {
  const [content, setContent] = useState('');
  const [posting, setPosting] = useState(false);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/social/posts`, { content, post_type: 'text' }, { headers: { Authorization: `Bearer ${token}` } });
      setContent('');
      toast.success('Posted!');
      onPost();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3 mb-4" data-testid="feed-post-box">
      <div className="flex items-start gap-3">
        <Avatar className="w-8 h-8 shrink-0">
          <AvatarFallback className="bg-purple-500/20 text-purple-400 text-xs">{user.name?.charAt(0)}</AvatarFallback>
        </Avatar>
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="What's happening in the Twin Cities?"
          className="flex-1 bg-transparent border-none outline-none resize-none text-sm placeholder:text-gray-500"
          rows={2}
          data-testid="feed-post-input"
        />
      </div>
      <div className="flex justify-end mt-2">
        <Button size="sm" onClick={handleSubmit} disabled={!content.trim() || posting} data-testid="feed-post-submit">
          <Send size={14} className="mr-1" /> Post
        </Button>
      </div>
    </div>
  );
};

// Individual feed post card
const FeedPostCard = ({ post, user, token, index, onRefresh }) => {
  const [liked, setLiked] = useState(post.is_liked);
  const [likesCount, setLikesCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');

  const handleLike = async () => {
    if (!token) { toast.error('Please log in'); return; }
    try {
      const res = await axios.post(`${API}/social/posts/${post.id}/like`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setLiked(res.data.liked);
      setLikesCount(res.data.likes_count);
    } catch { toast.error('Failed to like'); }
  };

  const handleShare = async () => {
    if (!token) { toast.error('Please log in to share'); return; }
    try {
      await axios.post(`${API}/social/posts/${post.id}/share`, { post_id: post.id }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Reshared!');
    } catch { toast.error('Failed to share'); }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !token) return;
    try {
      const res = await axios.post(`${API}/social/posts/${post.id}/comments`, { content: newComment }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch { toast.error('Failed to comment'); }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API}/social/posts/${post.id}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      setComments(res.data.comments || []);
    } catch { }
  };

  const handleDelete = async () => {
    if (!confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/social/posts/${post.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Post deleted');
      onRefresh();
    } catch { toast.error('Failed to delete'); }
  };

  const isOwner = user?.id === post.author_id || user?.role === 'admin';
  const timeAgo = getTimeAgo(post.created_at);

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
      className="rounded-xl border border-border bg-card overflow-hidden"
      data-testid={`feed-post-${post.id}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-3">
        <Link to={`/u/${post.author_id}`} className="flex items-center gap-2 hover:opacity-80 transition">
          <Avatar className="w-9 h-9">
            <AvatarImage src={post.author_profile_pic} />
            <AvatarFallback className="text-xs">{(post.author_display_name || post.author_name)?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-sm font-semibold">{post.author_display_name || post.author_name}</p>
            <p className="text-[10px] text-gray-500">{timeAgo} · <span className="capitalize">{post.author_role}</span></p>
          </div>
        </Link>
        {isOwner && (
          <button onClick={handleDelete} className="p-1.5 rounded-lg text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition" data-testid={`feed-delete-${post.id}`}>
            <Trash2 size={14} />
          </button>
        )}
      </div>

      {/* Content */}
      <div className="px-3 pb-2">
        <p className="text-sm whitespace-pre-wrap">{post.content}</p>
      </div>

      {/* Image */}
      {post.image_url && (
        <div className="px-3 pb-2">
          <img src={post.image_url} alt="" className="w-full rounded-lg max-h-96 object-cover" />
        </div>
      )}

      {/* Event tag */}
      {post.post_type === 'event' && post.event_date && (
        <div className="mx-3 mb-2 px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center gap-2">
          <Calendar size={14} className="text-purple-400" />
          <span className="text-xs text-purple-300">{post.event_date} {post.event_venue && `at ${post.event_venue}`}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 px-3 pb-3 pt-1 border-t border-border/30">
        <button
          onClick={handleLike}
          className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs transition ${liked ? 'text-red-400 bg-red-500/10' : 'text-gray-500 hover:text-red-400 hover:bg-red-500/5'}`}
          data-testid={`feed-like-${post.id}`}
        >
          <Heart size={14} fill={liked ? 'currentColor' : 'none'} /> {likesCount}
        </button>
        <button
          onClick={() => { if (!showComments) fetchComments(); setShowComments(!showComments); }}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-blue-400 hover:bg-blue-500/5 transition"
          data-testid={`feed-comment-toggle-${post.id}`}
        >
          <MessageCircle size={14} /> {post.comments_count || 0}
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:text-green-400 hover:bg-green-500/5 transition"
          data-testid={`feed-share-${post.id}`}
        >
          <Repeat2 size={14} /> {post.shares_count || 0}
        </button>
      </div>

      {/* Comments */}
      <AnimatePresence>
        {showComments && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-border/50 overflow-hidden">
            <div className="p-3 space-y-3">
              <form onSubmit={handleComment} className="flex gap-2">
                <Input value={newComment} onChange={e => setNewComment(e.target.value)} placeholder="Write a comment..." className="flex-1 bg-white/5 border-white/10 text-sm h-8" data-testid={`feed-comment-input-${post.id}`} />
                <Button type="submit" size="sm" disabled={!newComment.trim()} className="h-8 px-3" data-testid={`feed-comment-submit-${post.id}`}>
                  <Send size={12} />
                </Button>
              </form>
              {comments.map(c => (
                <div key={c.id} className="flex gap-2">
                  <Avatar className="w-6 h-6 shrink-0">
                    <AvatarImage src={c.author_profile_pic} />
                    <AvatarFallback className="text-[10px]">{(c.author_display_name || c.author_name)?.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-xs">
                      <span className="font-semibold mr-1">{c.author_display_name || c.author_name}</span>
                      {c.content}
                    </p>
                    <p className="text-[10px] text-gray-600 mt-0.5">{getTimeAgo(c.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Shared/reshared post card
const SharedPostCard = ({ share, user, token, index }) => {
  if (!share.original_post) return null;
  const post = share.original_post;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
      className="rounded-xl border border-border bg-card overflow-hidden"
      data-testid={`feed-share-${share.id}`}
    >
      {/* Reshare header */}
      <div className="flex items-center gap-2 px-3 pt-2 text-xs text-gray-500">
        <Repeat2 size={12} />
        <Link to={`/u/${share.user_id}`} className="hover:text-purple-400 transition">
          {share.sharer_name}
        </Link>
        <span>reshared</span>
      </div>
      {share.comment && (
        <p className="px-3 pt-1 text-sm text-gray-400 italic">"{share.comment}"</p>
      )}

      {/* Original post content */}
      <div className="mt-2 mx-3 mb-3 rounded-lg border border-border/50 bg-black/20 overflow-hidden">
        <div className="flex items-center gap-2 p-2">
          <Avatar className="w-7 h-7">
            <AvatarFallback className="text-[10px]">{post.author_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-xs font-semibold">{post.author_name}</p>
            <p className="text-[9px] text-gray-500 capitalize">{post.author_role}</p>
          </div>
        </div>
        <div className="px-2 pb-2">
          <p className="text-sm">{post.content}</p>
        </div>
        {post.image_url && (
          <img src={post.image_url} alt="" className="w-full max-h-48 object-cover" />
        )}
      </div>
    </motion.div>
  );
};

function getTimeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export default FeedPage;
